import { useCallback } from 'react';

import { IntentCreationCallbackParams } from '@stripe/stripe-react-native';

/**
 * This is the function called by the Stripe SDK to confirm the payment.
 */
export const useStripeConfirmHandler = (params: UseStripeConfirmHandlerParams) => {
  const {
    isAsyncPaymentsEnabled,
    createPaymentIntent,
    trackStripeUsage,
    onOrderReadyForPayment,
    onPaymentIntentCreated,
  } = params;

  const stripeConfirmHandler = useCallback(
    async (result: PaymentResult, shouldSetupFutureUsage: boolean, callback: Callback) => {
      const { id, paymentMethodType } = result;

      if (!isAsyncPaymentsEnabled) {
        trackStripeUsage({ event: 'submit_payment_method' });
        callback(SUCCESS_COMPLETE);
        onOrderReadyForPayment?.(id);
        return;
      }

      const paymentIntent = await createPaymentIntent(
        id,
        paymentMethodType,
        shouldSetupFutureUsage,
      );

      if (!paymentIntent) {
        callback(STRIPE_FAILURE);
        return;
      }

      onPaymentIntentCreated?.(paymentIntent);
      callback({ clientSecret: paymentIntent.clientSecret });
    },
    [
      isAsyncPaymentsEnabled,
      createPaymentIntent,
      trackStripeUsage,
      onOrderReadyForPayment,
      onPaymentIntentCreated,
    ],
  );

  return { stripeConfirmHandler };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentResult = {
  id: string;
  paymentMethodType: string;
  shouldSetupFutureUsage?: boolean;
};

type PaymentIntent = {
  orderId: string;
  paymentIntentId: string;
  clientSecret: string;
};

type Callback = (result: IntentCreationCallbackParams) => void;

type ConfirmPaymentIntent = (
  paymentMethodId: string,
  paymentMethodType: string,
  shouldSetupFutureUsage?: boolean,
) => Promise<PaymentIntent | undefined>;

type UseStripeConfirmHandlerParams = {
  isAsyncPaymentsEnabled: boolean;
  createPaymentIntent: ConfirmPaymentIntent;
  trackStripeUsage: (payload: Record<string, unknown>) => void;
  onOrderReadyForPayment?: (paymentMethodId: string) => void;
  onPaymentIntentCreated?: (paymentIntent: PaymentIntent) => void;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NO_INTENT_COMPLETE = 'COMPLETE_WITHOUT_CONFIRMING_INTENT';
const SUCCESS_COMPLETE = { clientSecret: NO_INTENT_COMPLETE };
const STRIPE_FAILURE = {
  error: { code: 'Failed' as const, message: 'Stripe failed to confirm the payment' },
};
